/**
 * PlatformOrgMembersPage — Vincent #196 (2026-05-27)
 *
 * Full member management for a single organization, accessible from
 * "Plateforme Knoria > Personnes > Entreprises > {org}".
 *
 * Admin / VP / CEO / Président peuvent :
 *   - Ajouter un utilisateur manuellement (avec mot de passe temporaire)
 *   - Inviter par courriel (avec ou sans coupon)
 *   - Modifier le titre / poste (dropdown)
 *   - Archiver / restaurer (soft delete — données conservées)
 *   - Révoquer / restaurer l'accès (bloquer le login)
 *   - Révoquer les coupons utilisés
 *   - Supprimer définitivement (confirmation rouge par email exact)
 */
import { useCallback, useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import {
  ArrowLeft, UserPlus, EnvelopeSimple, Archive, ArrowCounterClockwise,
  ShieldSlash, Trash, Tag, FloppyDisk, X, Buildings, SpinnerGap, Users,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { API_BASE as API } from "../lib/apiBase";
import { useAuth } from "../contexts/AuthContext";
import { getCanonicalRoles, getCanonicalLabel, roleLabelsFor } from "../lib/canonicalRoles";
import { useLang } from "../contexts/LanguageContext";

// Vincent 2026-06-01 — Source unique : 8 rôles canoniques de "Gestion des rôles".
// Plus de president/ceo/vp séparés dans le UI (tous absorbés par Admin).
// Le ROLE_OPTIONS legacy est conservé en fallback si roleLabelsFor(user, lang) indispo.
const FALLBACK_ROLE_OPTIONS = [
  { value: "viewer", label: "Visiteur", label_en: "Viewer" },
  { value: "employee", label: "Employé", label_en: "Employee" },
  { value: "employee_new", label: "Nouvel employé", label_en: "New employee" },
  { value: "employee_trained", label: "Employé formé", label_en: "Trained employee" },
  { value: "employee_advanced", label: "Employé avancé", label_en: "Advanced employee" },
  { value: "concepteur", label: "Concepteur", label_en: "Designer" },
  { value: "admin", label: "Admin", label_en: "Admin" },
  { value: "founder", label: "Fondateur", label_en: "Founder" },
];

const ROLE_OPTIONS = FALLBACK_ROLE_OPTIONS;

const STATUS_BADGE = {
  approved: { label: "Actif", label_en: "Active", color: "bg-teal-100 text-teal-700" },
  pending_approval: { label: "En attente", label_en: "Pending", color: "bg-amber-100 text-amber-700" },
  rejected: { label: "Refusé", label_en: "Rejected", color: "bg-rose-100 text-rose-700" },
  archived: { label: "Archivé", label_en: "Archived", color: "bg-zinc-200 text-zinc-600" },
  deleted: { label: "Supprimé", label_en: "Deleted", color: "bg-zinc-300 text-zinc-700" },
};

export default function PlatformOrgMembersPage() {
  // Tâche 1387 — les libellés de rôles suivent la langue de l'écran.
  const { tr, lang } = useLang();
  const { orgId } = useParams();
  const [data, setData] = useState({ org: null, members: [], pending_invites: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/platform/orgs/${orgId}/members`, { withCredentials: true });
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur de chargement", "Loading failed"));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const setRole = async (member, newRole) => {
    if (newRole === member.org_role) return;
    const ok = await confirmDialog(
      tr(
        `Changer le rôle de ${member.name || member.email} en « ${ROLE_OPTIONS.find(r => r.value === newRole)?.label || newRole} » ?`,
        `Change the role of ${member.name || member.email} to “${ROLE_OPTIONS.find(r => r.value === newRole)?.label_en || newRole}”?`,
      ),
      { confirmLabel: tr("Changer le rôle", "Change the role") },
    );
    if (!ok) return;
    setBusy(member.id);
    try {
      await axios.put(`${API}/api/platform/orgs/${orgId}/members/${member.id}/role`, { org_role: newRole }, { withCredentials: true });
      toast.success(tr("Rôle modifié", "Role changed"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur", "Error"));
    } finally { setBusy(null); }
  };

  const archive = async (member) => {
    const ok = await confirmDialog(
      `Archiver ${member.name || member.email} ? Le compte sera désactivé mais TOUTES ses données seront conservées.`,
      { danger: true, confirmLabel: tr("Archiver", "Archive") },
    );
    if (!ok) return;
    setBusy(member.id);
    try {
      await axios.post(`${API}/api/platform/orgs/${orgId}/members/${member.id}/archive`, {}, { withCredentials: true });
      toast.success(tr("Membre archivé", "Member archived"));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erreur", "Error")); }
    finally { setBusy(null); }
  };

  const restore = async (member) => {
    setBusy(member.id);
    try {
      await axios.post(`${API}/api/platform/orgs/${orgId}/members/${member.id}/restore`, {}, { withCredentials: true });
      toast.success(tr("Membre restauré", "Member restored"));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erreur", "Error")); }
    finally { setBusy(null); }
  };

  const revokeAccess = async (member) => {
    const ok = await confirmDialog(
      `Révoquer l'accès de ${member.name || member.email} ? Le compte sera bloqué (login impossible).`,
      { danger: true, confirmLabel: tr("Révoquer l'accès", "Revoke access") },
    );
    if (!ok) return;
    setBusy(member.id);
    try {
      await axios.post(`${API}/api/platform/orgs/${orgId}/members/${member.id}/revoke-access`, {}, { withCredentials: true });
      toast.success(tr("Accès révoqué", "Access revoked"));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erreur", "Error")); }
    finally { setBusy(null); }
  };

  const restoreAccess = async (member) => {
    setBusy(member.id);
    try {
      await axios.post(`${API}/api/platform/orgs/${orgId}/members/${member.id}/restore-access`, {}, { withCredentials: true });
      toast.success(tr("Accès restauré", "Access restored"));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erreur", "Error")); }
    finally { setBusy(null); }
  };

  const revokeCoupons = async (member) => {
    const ok = await confirmDialog(
      `Désactiver TOUS les coupons utilisés par ${member.name || member.email} ?`,
      { danger: true, confirmLabel: tr("Désactiver les coupons", "Deactivate the coupons") },
    );
    if (!ok) return;
    setBusy(member.id);
    try {
      const r = await axios.post(`${API}/api/platform/orgs/${orgId}/members/${member.id}/revoke-coupons`, {}, { withCredentials: true });
      toast.success(`${(r.data.deactivated_codes || []).length} coupon(s) désactivé(s)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erreur", "Error")); }
    finally { setBusy(null); }
  };

  const hardDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirm.trim().toLowerCase() !== deleteTarget.email.toLowerCase()) {
      toast.error(tr("L'email tapé ne correspond pas", "The email you typed does not match"));
      return;
    }
    setBusy(deleteTarget.id);
    try {
      const r = await axios.delete(`${API}/api/platform/orgs/${orgId}/members/${deleteTarget.id}`, {
        data: { confirm_email: deleteConfirm },
        withCredentials: true,
      });
      toast.success(`Membre supprimé. ${r.data.total_records_deleted} enregistrement(s) effacé(s).`);
      setDeleteTarget(null);
      setDeleteConfirm("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erreur", "Error")); }
    finally { setBusy(null); }
  };

  if (loading) {
    return <div className="p-12 text-center"><SpinnerGap size={32} className="animate-spin text-[#002FA7] mx-auto" /></div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="platform-org-members-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/dashboard/platform/organizations" className="text-zinc-400 hover:text-zinc-700" data-testid="back-to-orgs">
            <ArrowLeft size={22} />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2 font-['Outfit']">
              <Buildings size={26} className="text-[#002FA7]" /> {data.org?.name || orgId}
            </h1>
            <p className="text-xs text-zinc-500">
              {data.member_count} membre(s) · Limite : {data.org?.max_users || "—"}
              {data.pending_invites?.length > 0 && ` · ${data.pending_invites.length} invitation(s) en attente`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowInvite(true)} variant="outline" className="gap-1.5" data-testid="btn-invite-member">
            <EnvelopeSimple size={16} /> {tr("Inviter par courriel", "Invite by email")}
          </Button>
          <Button onClick={() => setShowAdd(true)} className="gap-1.5 bg-[#002FA7] hover:bg-[#002280]" data-testid="btn-add-member">
            <UserPlus size={16} /> Ajouter manuellement
          </Button>
        </div>
      </div>

      {/* Pending invites */}
      {data.pending_invites?.length > 0 && (
        <section className="mb-6">
          <h2 className="text-xs uppercase font-bold text-zinc-500 mb-2">Invitations en attente</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-lg divide-y divide-amber-100" data-testid="pending-invites-list">
            {data.pending_invites.map(inv => (
              <div key={inv.id} className="p-3 flex items-center justify-between text-sm" data-testid={`invite-${inv.email}`}>
                <div>
                  <span className="font-medium">{inv.email}</span>
                  <span className="ml-2 px-1.5 py-0.5 bg-amber-100 rounded text-xs">{ROLE_OPTIONS.find(r => r.value === inv.role)?.label || inv.role}</span>
                  {inv.coupon_code && <span className="ml-2 px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">Coupon: {inv.coupon_code}</span>}
                </div>
                <span className="text-xs text-zinc-500">{new Date(inv.created_at).toLocaleDateString("fr-FR")}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Members table */}
      <section>
        <h2 className="text-xs uppercase font-bold text-zinc-500 mb-2 flex items-center gap-2"><Users size={14} /> Membres</h2>
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="members-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">Nom / Email</th>
                <th className="px-3 py-2 text-left">{tr("Titre / Poste", "Title / Position")}</th>
                <th className="px-3 py-2 text-center">Statut</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.members.map(m => {
                const status = STATUS_BADGE[m.account_status] || { label: m.account_status || "?", color: "bg-zinc-100 text-zinc-700" };
                const isBlocked = !!m.account_blocked;
                return (
                  <tr key={m.id} data-testid={`member-row-${m.email}`} className={m.is_secondary ? "bg-blue-50/40" : ""}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{m.name || "—"}</div>
                      <div className="text-xs text-zinc-500">{m.email}</div>
                      {m.is_secondary && <div className="text-[10px] text-blue-600 mt-0.5">Entreprise secondaire</div>}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={m.org_role || "employee"}
                        onChange={(e) => setRole(m, e.target.value)}
                        disabled={busy === m.id}
                        className="text-xs px-1.5 py-1 border border-zinc-200 rounded bg-white"
                        data-testid={`role-select-${m.email}`}
                      >
                        {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{lang === "en" ? r.label_en : r.label}</option>)}
                        {!ROLE_OPTIONS.find(r => r.value === m.org_role) && <option value={m.org_role}>{m.org_role}</option>}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${status.color}`} data-testid={`status-${m.email}`}>{lang === "en" ? status.label_en : status.label}</span>
                      {isBlocked && <span className="ml-1 px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded text-[10px] font-bold uppercase">{tr("Bloqué", "Blocked")}</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {m.account_status === "archived" ? (
                          <button onClick={() => restore(m)} disabled={busy === m.id} className="p-1.5 text-teal-600 hover:bg-teal-50 rounded" title={tr("Restaurer", "Restore")} data-testid={`restore-${m.email}`}>
                            <ArrowCounterClockwise size={14} />
                          </button>
                        ) : (
                          <button onClick={() => archive(m)} disabled={busy === m.id} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title={tr("Archiver", "Archive")} data-testid={`archive-${m.email}`}>
                            <Archive size={14} />
                          </button>
                        )}
                        {isBlocked ? (
                          <button onClick={() => restoreAccess(m)} disabled={busy === m.id} className="p-1.5 text-teal-600 hover:bg-teal-50 rounded" title={tr("Restaurer l'accès", "Restore access")} data-testid={`restore-access-${m.email}`}>
                            <ShieldSlash size={14} />
                          </button>
                        ) : (
                          <button onClick={() => revokeAccess(m)} disabled={busy === m.id} className="p-1.5 text-orange-600 hover:bg-orange-50 rounded" title={tr("Révoquer l'accès", "Revoke access")} data-testid={`revoke-access-${m.email}`}>
                            <ShieldSlash size={14} />
                          </button>
                        )}
                        <button onClick={() => revokeCoupons(m)} disabled={busy === m.id} className="p-1.5 text-violet-600 hover:bg-violet-50 rounded" title={tr("Révoquer ses coupons", "Revoke their coupons")} data-testid={`revoke-coupons-${m.email}`}>
                          <Tag size={14} />
                        </button>
                        <button onClick={() => { setDeleteTarget(m); setDeleteConfirm(""); }} disabled={busy === m.id} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" title={tr("Supprimer définitivement", "Delete permanently")} data-testid={`hard-delete-${m.email}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {data.members.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-zinc-400">{tr("Aucun membre", "No member")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Add member modal */}
      {showAdd && <AddMemberModal orgId={orgId} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {showInvite && <InviteMemberModal orgId={orgId} onClose={() => setShowInvite(false)} onSaved={() => { setShowInvite(false); load(); }} />}

      {/* Hard delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent data-testid="hard-delete-dialog" className="border-2 border-rose-500">
          <DialogHeader>
            <DialogTitle className="text-rose-600 flex items-center gap-2">
              <Trash size={22} weight="fill" /> {tr("Suppression DÉFINITIVE", "PERMANENT deletion")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="p-3 bg-rose-50 border border-rose-200 rounded text-rose-800">
              <strong>{tr("⚠️ Action irréversible.", "⚠️ This cannot be undone.")}</strong>{" "}
              {tr(
                "Toutes les données de cet utilisateur seront effacées (notifications, progression, messages, certifications…). Pour les coupons et le journal d'audit, l'enregistrement de l'action est conservé (Loi 25).",
                "Every piece of this person's data will be erased (notifications, progress, messages, certifications…). For coupons and the audit log, the record of the action is kept (Law 25).",
              )}
            </div>
            <p>{tr("Tapez l'email exact ", "Type the exact email ")}<code className="bg-zinc-100 px-1.5 py-0.5 rounded font-mono">{deleteTarget?.email}</code>{tr(" pour confirmer :", " to confirm:")}</p>
            <Input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder={deleteTarget?.email}
              className="font-mono"
              autoFocus
              data-testid="hard-delete-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={busy === deleteTarget?.id} data-testid="hard-delete-cancel">{tr("Annuler", "Cancel")}</Button>
            <Button
              onClick={hardDelete}
              disabled={busy === deleteTarget?.id || deleteConfirm.trim().toLowerCase() !== deleteTarget?.email.toLowerCase()}
              className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
              data-testid="hard-delete-confirm-btn"
            >
              {busy === deleteTarget?.id ? <SpinnerGap size={14} className="animate-spin" /> : <Trash size={14} />}
              {tr("Supprimer définitivement", "Delete permanently")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function AddMemberModal({ orgId, onClose, onSaved }) {
  // Leçon 1373 — un sous-composant n'hérite pas du `tr` de sa page.
  const { tr, lang } = useLang();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("employee_trained");
  const [tempPwd, setTempPwd] = useState(() => `Knoria-${Math.floor(1000 + Math.random() * 9000)}!`);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async () => {
    if (!email || !name || tempPwd.length < 8) { toast.error(tr("Tous les champs requis", "Every field is required")); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/platform/orgs/${orgId}/members/add-manual`, { email, name, org_role: role, temp_password: tempPwd }, { withCredentials: true });
      setCreated({ email, tempPwd });
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur", "Error"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && (created ? onSaved() : onClose())}>
      <DialogContent data-testid="add-member-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus size={20} /> {tr("Ajouter manuellement un membre", "Add a member manually")}</DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="space-y-3 text-sm" data-testid="add-member-success">
            <div className="p-3 bg-teal-50 border border-teal-200 rounded text-teal-800">
              <strong>{tr("Compte créé pour ", "Account created for ")}{created.email}</strong>
              <p className="mt-1">{tr(
                "Communique ce mot de passe temporaire à l'utilisateur — il devra le changer à sa première connexion :",
                "Pass this temporary password on — the person will have to change it at first sign-in:",
              )}</p>
            </div>
            <div className="font-mono text-lg bg-zinc-100 p-3 rounded text-center select-all" data-testid="created-temp-pwd">{created.tempPwd}</div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Email *</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="off" data-testid="add-member-email" />
            </div>
            <div>
              <label className="text-xs font-semibold">{tr("Nom complet *", "Full name *")}</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="add-member-name" />
            </div>
            <div>
              <label className="text-xs font-semibold">{tr("Titre / Poste", "Title / Position")}</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="add-member-role">
                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{lang === "en" ? r.label_en : r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">{tr("Mot de passe temporaire * (min 8)", "Temporary password * (min 8)")}</label>
              <Input value={tempPwd} onChange={(e) => setTempPwd(e.target.value)} className="font-mono" data-testid="add-member-tempPwd" />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          {created ? (
            <Button onClick={onSaved} className="bg-[#002FA7] hover:bg-[#002280]" data-testid="add-member-done">{tr("Terminé", "Done")}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={saving}><X size={14} /> {tr("Annuler", "Cancel")}</Button>
              <Button onClick={submit} disabled={saving} className="gap-1.5 bg-[#002FA7] hover:bg-[#002280]" data-testid="add-member-submit">
                {saving ? <SpinnerGap size={14} className="animate-spin" /> : <FloppyDisk size={14} />} {tr("Créer le compte", "Create the account")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function InviteMemberModal({ orgId, onClose, onSaved }) {
  // Leçon 1373 — un sous-composant n'hérite pas du `tr` de sa page.
  const { tr, lang } = useLang();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("employee_trained");
  const [couponCode, setCouponCode] = useState("");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [saving, setSaving] = useState(false);
  // Vincent #194 (Eric 2026-05-28) — Sans clé Resend l'email ne part PAS.
  // Au lieu de dire "envoyé" et que rien n'arrive, on affiche le lien
  // directement à l'admin pour qu'il puisse le copier/coller manuellement.
  const [created, setCreated] = useState(null); // {url, email, coupon, emailSent}

  useEffect(() => {
    axios.get(`${API}/api/coupons`, { withCredentials: true })
      .then(r => setAvailableCoupons((r.data.coupons || []).filter(c => c.is_active)))
      .catch(() => setAvailableCoupons([]));
  }, []);

  const submit = async () => {
    if (!email) { toast.error(tr("Email requis", "Email required")); return; }
    setSaving(true);
    try {
      const payload = { email, role };
      if (couponCode) payload.coupon_code = couponCode;
      const r = await axios.post(`${API}/api/platform/orgs/${orgId}/members/invite`, payload, { withCredentials: true });
      const inviteUrl = `${window.location.origin}/register?token=${r.data.token}${couponCode ? `&coupon=${couponCode}` : ""}`;
      setCreated({
        url: inviteUrl,
        email,
        coupon: couponCode,
        emailSent: !!r.data.email_sent,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur", "Error"));
    } finally { setSaving(false); }
  };

  const copyLink = () => {
    if (!created?.url) return;
    navigator.clipboard?.writeText(created.url).then(
      () => toast.success(tr("Lien copié dans le presse-papier", "Link copied to the clipboard")),
      () => toast.error(tr("Impossible de copier — sélectionnez manuellement", "Copy failed — select it manually")),
    );
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && (created ? onSaved() : onClose())}>
      <DialogContent data-testid="invite-member-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><EnvelopeSimple size={20} /> {tr("Inviter par courriel", "Invite by email")}</DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="space-y-3" data-testid="invite-member-success">
            {created.emailSent ? (
              <div className="p-3 bg-teal-50 border border-teal-200 rounded text-sm text-teal-800">
                <strong>{tr("✓ Courriel envoyé à ", "✓ Email sent to ")}{created.email}</strong>
                <p className="text-xs mt-1">{tr(
                  "Si la personne ne le reçoit pas dans 5 minutes, transmets-lui le lien ci-dessous manuellement.",
                  "If it has not arrived within 5 minutes, send the link below by hand.",
                )}</p>
              </div>
            ) : (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                <strong>{tr("⚠️ Lien d'invitation créé", "⚠️ Invitation link created")}</strong>
                <p className="text-xs mt-1">
                  {tr(
                    "L'envoi automatique de courriels n'est pas configuré pour cette organisation (clé Resend manquante).",
                    "Automatic email sending is not configured for this organisation (Resend key missing).",
                  )}
                  <strong>{tr(" Copie le lien ci-dessous", " Copy the link below")}</strong>
                  {tr(" et transmets-le à ", " and send it to ")}<strong>{created.email}</strong>
                  {tr(" par tes moyens habituels (courriel, SMS, Messenger…).", " however you usually reach them (email, SMS, Messenger…).")}
                </p>
              </div>
            )}
            <label className="text-xs font-semibold">{tr("Lien d'invitation (valide 7 jours) :", "Invitation link (valid for 7 days):")}</label>
            <div className="flex gap-2">
              <Input value={created.url} readOnly className="font-mono text-xs select-all" data-testid="invite-link-input" />
              <Button onClick={copyLink} className="gap-1.5 shrink-0 bg-[#002FA7] hover:bg-[#002280]" data-testid="copy-invite-link">
                <FloppyDisk size={14} /> {tr("Copier", "Copy")}
              </Button>
            </div>
            {created.coupon && (
              <p className="text-[11px] text-teal-700">
                {tr("Le coupon ", "Coupon ")}<strong>{created.coupon}</strong>{tr(" sera automatiquement appliqué à l'inscription.", " will be applied automatically at sign-up.")}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Email *</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="invite-email" />
            </div>
            <div>
              <label className="text-xs font-semibold">{tr("Titre / Poste", "Title / Position")}</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="invite-role">
                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{lang === "en" ? r.label_en : r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">{tr("Coupon associé", "Linked coupon")} <span className="text-zinc-400">{tr("(optionnel)", "(optional)")}</span></label>
              <select value={couponCode} onChange={(e) => setCouponCode(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="invite-coupon">
                <option value="">{tr("— Aucun coupon —", "— No coupon —")}</option>
                {availableCoupons.map(c => <option key={c.id} value={c.code}>{`${c.code} (${c.discount_percent}%)`}</option>)}
              </select>
              <p className="text-[11px] text-zinc-500 mt-1">{tr("L'utilisateur sera auto-rattaché à votre entreprise dès son inscription.", "The person will be attached to your company automatically as soon as they sign up.")}</p>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          {created ? (
            <Button onClick={onSaved} className="bg-[#002FA7] hover:bg-[#002280]" data-testid="invite-done-btn">{tr("Terminé", "Done")}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={saving}><X size={14} /> {tr("Annuler", "Cancel")}</Button>
              <Button onClick={submit} disabled={saving} className="gap-1.5 bg-[#002FA7] hover:bg-[#002280]" data-testid="invite-submit">
                {saving ? <SpinnerGap size={14} className="animate-spin" /> : <EnvelopeSimple size={14} />} {tr("Créer l'invitation", "Create the invitation")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
